import { __assign, __read, __rest, __spread } from "tslib";
import React, { createContext, useEffect, useRef, useState, forwardRef, useContext, } from "react";
import classNames from "classnames";
import { callBoth } from "../../../_util/call-both";
import { mergeStyle } from "../../../_util/merge-style";
import { mergeRefs } from "../../../_util/merge-refs";
import { getScrollBarSize } from "../../../_util/get-scrollbar-size";
import { useConfig } from "../../../_util/config-context";
/**
 * 滚动 Context 实例
 */
var ScrollContext = createContext(null);
ScrollContext.displayName = "Scrollable";
/**
 * 注入到外层容器，提供滚动上下文
 */
export function ScrollableTable(_a) {
    var table = _a.table, scrollHeightFactor = _a.scrollHeightFactor, _b = _a.onScrollBottom, onScrollBottom = _b === void 0 ? function () { return null; } : _b, props = __rest(_a, ["table", "scrollHeightFactor", "onScrollBottom"]);
    var classPrefix = useConfig().classPrefix;
    // 当前滚动区域是否在滚动状态
    var _c = __read(useState(false), 2), bodyHasScroll = _c[0], setBodyHasScroll = _c[1];
    // 滚动区域的 DOM 引用
    var bodyRef = useRef(null);
    // 决定滚动高度的因子改变后，重新检测滚动状态，设置到 bodyHasScroll 中
    useEffect(function () {
        var bodyBox = bodyRef.current;
        if (bodyBox) {
            // 滚动状态下，scrollHeight > clientHeight
            var nextBodyHasScroll = bodyBox.scrollHeight > bodyBox.clientHeight;
            if (bodyHasScroll !== nextBodyHasScroll) {
                setBodyHasScroll(nextBodyHasScroll);
            }
        }
    }, [bodyHasScroll, scrollHeightFactor]);
    function handleBodyScroll(event) {
        var bodyBox = event.target;
        var scrollHeight = bodyBox.scrollHeight, scrollTop = bodyBox.scrollTop, clientHeight = bodyBox.clientHeight;
        if (bodyHasScroll &&
            scrollHeight <= Math.ceil(clientHeight + scrollTop + 1)) {
            onScrollBottom(event);
        }
    }
    // 滚动上下文
    var scrollContext = {
        // 提供给 body 使用，提供 body DOM 实例
        setBodyRef: function (body) {
            bodyRef.current = body;
        },
        // 提供给 head 使用，通过滚动状态决定是否增加右侧间距
        bodyHasScroll: bodyHasScroll,
        onScrollCapture: handleBodyScroll,
    };
    return (React.createElement(ScrollContext.Provider, { value: scrollContext }, React.cloneElement(table, __assign(__assign({}, props), { 
        // 设计通过提供 tea-table--scrollable 类来开启滚动区域的 overflow: auto
        className: classNames(table.props.className, classPrefix + "-table--scrollable") }))));
}
/**
 * 包装表格内容区（滚动部分），回调内容区 DOM 的实例
 */
export var ScrollableTableBody = forwardRef(function (_a, ref) {
    var body = _a.body, style = _a.style, bodyDeps = _a.bodyDeps, scrollToTopOnChange = _a.scrollToTopOnChange, props = __rest(_a, ["body", "style", "bodyDeps", "scrollToTopOnChange"]);
    var bodyRef = useRef(null);
    useEffect(function () {
        if (scrollToTopOnChange && bodyRef.current) {
            bodyRef.current.scrollTop = 0;
            bodyRef.current.scrollLeft = 0;
        }
    }, __spread([scrollToTopOnChange], bodyDeps)); // eslint-disable-line react-hooks/exhaustive-deps
    var _b = useContext(ScrollContext), setBodyRef = _b.setBodyRef, onScrollCapture = _b.onScrollCapture;
    return React.cloneElement(body, __assign(__assign({}, props), { 
        // 使用 mergeRefs 可以使得其他插件的 ref 也能被调用到
        ref: mergeRefs(setBodyRef, body.ref, ref, bodyRef), style: mergeStyle(body.props.style, style), onScrollCapture: callBoth(body.props.onScrollCapture, onScrollCapture) }));
});
/**
 * 包装表格头部，通过滚动状态调整右侧间距
 */
export var ScrollableTableHead = forwardRef(function (_a, ref) {
    var head = _a.head, props = __rest(_a, ["head"]);
    var bodyHasScroll = useContext(ScrollContext).bodyHasScroll;
    return React.cloneElement(head, __assign(__assign({}, props), { ref: mergeRefs(head.ref, ref), style: mergeStyle(head.props.style, {
            marginRight: bodyHasScroll ? getScrollBarSize() : null,
        }) }));
});
ScrollableTable.displayName = "ScrollableTable";
//# sourceMappingURL=ScrollableTable.js.map